import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import { 
  TrendingUp, 
  CheckCircle2,
  Info,
  ShieldCheck,
  MessageSquare,
  Smile,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Clock,
  Star,
  TrendingDown,
  Package,
  Tag,
  RotateCcw,
  CreditCard,
  HelpCircle,
  ChevronDown
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import Loader from '../components/Loader';
import './ShopHealthPage.css';
import { useAuth } from '../context/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ShopHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spsViewMode, setSpsViewMode] = useState('3D'); // '2D' for Line, '3D' for Bar
  const { selectedShopId, dateRange } = useAuth();

  useEffect(() => {
    fetchShopHealthMetrics();
  }, [selectedShopId, dateRange]);

  const fetchShopHealthMetrics = async () => {
    try {
      setLoading(true);
      let url = `/shop-health-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching shop health metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const parseAvgResponse = (timeStr) => {
    if (!timeStr || timeStr === 'N/A') return 0;
    // Handle cases like "45 hours", "2h 15m", "1.5d"
    const dMatch = timeStr.match(/(\d+(\.\d+)?)\s*d/i);
    const hMatch = timeStr.match(/(\d+(\.\d+)?)\s*h/i);
    const mMatch = timeStr.match(/(\d+(\.\d+)?)\s*m/i);
    
    let totalHours = 0;
    if (dMatch) totalHours += parseFloat(dMatch[1]) * 24;
    if (hMatch) totalHours += parseFloat(hMatch[1]);
    if (mMatch) totalHours += parseFloat(mMatch[1]) / 60;
    
    // If no units found, try parsing as a raw number of hours
    if (totalHours === 0 && !isNaN(parseFloat(timeStr))) {
      totalHours = parseFloat(timeStr);
    }
    return totalHours;
  };


  const getScoreDetails = (score) => {
    if (score === null || score === undefined) return { color: '#666', label: 'Data Unavailable', border: 'rgba(255, 255, 255, 0.1)' };
    if (score > 4.1) return { color: '#10b981', label: 'Healthy Status', border: 'rgba(16, 185, 129, 0.2)' };
    if (score >= 3.6) return { color: '#eab308', label: 'Medium or caution range', border: 'rgba(234, 179, 8, 0.2)' };
    return { color: '#ef4444', label: 'Low or high-risk score', border: 'rgba(239, 68, 68, 0.2)' };
  };

  const scoreDetails = getScoreDetails(data?.sps_score);

  const spsDonutData = {
    datasets: [{
      data: [data?.sps_score || 0, 5 - (data?.sps_score || 0)],
      backgroundColor: [scoreDetails.color, '#111'],
      borderWidth: 0,
      cutout: '80%',
      circumference: 270,
      rotation: 225,
      borderRadius: 10
    }]
  };

  return (
    <UserLayout>
      <div className="page-relative-wrapper">
        <div className="shop-health-container">
        {/* Top Section */}
        <div className="health-top-grid">
          {/* SPS Score */}
          <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`} style={{ borderColor: scoreDetails.border }}>
            <div className="health-card-header">
              <span className="health-card-title">SPS Score</span>
              <Info size={16} color="#444" />
            </div>
            
            <div className="sps-score-container">
              <Doughnut 
                data={spsDonutData} 
                options={{ 
                    maintainAspectRatio: false, 
                    plugins: { legend: { display: false }, tooltip: { enabled: false } },
                    events: []
                }} 
              />
              <div className="sps-radial-text">
                <div className="sps-value" style={{ color: scoreDetails.color }}>{data?.sps_score !== null && data?.sps_score !== undefined ? data.sps_score.toFixed(1) : 'N/A'}</div>
                <div className="sps-max">/5.0</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="health-status-pill" style={{ 
                backgroundColor: `${scoreDetails.color}15`, 
                color: scoreDetails.color,
                borderColor: `${scoreDetails.color}30` 
              }}>
                <div className="status-dot" style={{ backgroundColor: scoreDetails.color }} />
                {scoreDetails.label}
              </div>
              
              <div style={{ width: '100%' }}>
                <div className="health-details-row">
                  <span>Performance</span>
                  <span className="health-details-value">Excellent</span>
                </div>
                <div className="health-details-row">
                  <span>Trend (30d)</span>
                  <span className="health-details-value" style={{ color: scoreDetails.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <TrendingUp size={14} /> +0.3
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Active Violations */}
          <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="health-card-header">
              <span className="health-card-title">Active Violations</span>
              <Info size={16} color="#444" />
            </div>

            <div className="violations-main-value" style={{ color: (data?.violations || []).some(v => v.count > 0) ? '#ef4444' : '#10b981' }}>
              {(data?.violations || []).reduce((acc, curr) => acc + (curr.count || 0), 0)}
            </div>
            <div className="violations-subtext">
              {(data?.violations || []).some(v => v.count > 0) ? 'Violations needing attention' : 'No violations detected'}
            </div>

            {(data?.violations || []).map((v, i) => (
              <div key={i} className="violation-card">
                <div className="violation-info">
                  <div className="violation-icon-base" style={{ backgroundColor: v.count > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)' }}>
                    {v.count > 0 ? <AlertTriangle size={18} color="#ef4444" /> : <CheckCircle2 size={18} color="#10b981" />}
                  </div>
                  <span className="violation-label">{v.label}</span>
                </div>
                <span className="violation-count">{v.count || 0}</span>
              </div>
            ))}
          </div>

          {/* Customer Metrics */}
          <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="health-card-header">
                <span className="health-card-title">Customer Metrics</span>
                <Info size={16} color="#444" />
            </div>

            {(() => {
              const avgHours = parseAvgResponse(data?.customer_metrics?.avg_response_time);
              let responseStatus = "Excellent";
              let responseColor = "#10b981"; // Green
              let progressWidth = "95%";

              if (avgHours > 40) {
                responseStatus = "Need Attention";
                responseColor = "#ef4444"; // Red
                progressWidth = "30%";
              } else if (avgHours >= 30) {
                responseStatus = "Good";
                responseColor = "#f59e0b"; // Orange
                progressWidth = "65%";
              } else {
                // For Excellent, width stays high
                progressWidth = Math.max(80, 100 - (avgHours / 30 * 20)) + "%";
              }

              return (
                <div className="metric-row">
                  <div className="metric-header">
                    <span className="metric-label">Avg. Response</span>
                    <span className="metric-value" style={{ color: responseColor }}>{data?.customer_metrics?.avg_response_time || 'N/A'}</span>
                  </div>
                  <div className="progress-bar-bg">
                    <div 
                        className="progress-bar-fill" 
                        style={{ width: progressWidth, background: `linear-gradient(90deg, ${responseColor} 0%, ${responseColor}cc 100%)` }} 
                    />
                  </div>
                  <div className="metric-footer">
                    <span className="metric-status" style={{ color: responseColor }}>{responseStatus}</span>
                  </div>
                </div>
              );
            })()}

            {(() => {
              const dissRateStr = data?.customer_metrics?.dissatisfaction_rate || '0%';
              const dissRateVal = parseFloat(dissRateStr.replace('%', ''));
              
              let status = "Excellent";
              let color = "#10b981"; // Green
              let progressWidth = Math.max(10, dissRateVal * 20) + "%"; // Progress increases with rate

              if (dissRateVal > 1) {
                status = "Need Attention";
                color = "#ef4444"; // Red
                progressWidth = "85%";
              } else if (dissRateVal >= 0.5) {
                status = "Good";
                color = "#f59e0b"; // Orange
                progressWidth = "55%";
              } else {
                // For Excellent, width stays low
                progressWidth = Math.max(10, dissRateVal * 40) + "%";
              }

              return (
                <div className="metric-row">
                  <div className="metric-header">
                    <span className="metric-label">Dissatisfaction Rate</span>
                    <span className="metric-value" style={{ color }}>{dissRateStr}</span>
                  </div>
                  <div className="progress-bar-bg">
                    <div 
                        className="progress-bar-fill" 
                        style={{ width: progressWidth, background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)` }} 
                    />
                  </div>
                  <div className="metric-footer">
                    <span className="metric-status" style={{ color }}>{status}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Middle Section: SPS Trend and AI Insights */}
        <div className="health-main-grid">
          <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
             <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px' }}>
               <span style={{ fontSize: '18px', fontWeight: '700' }}>SPS Score Trend</span>
               <div className="timeframe-group">
                 <button 
                  className={`timeframe-btn ${spsViewMode === '2D' ? 'active' : ''}`}
                  onClick={() => setSpsViewMode('2D')}
                 >
                  2D
                 </button>
                 <button 
                  className={`timeframe-btn ${spsViewMode === '3D' ? 'active' : ''}`}
                  onClick={() => setSpsViewMode('3D')}
                 >
                  3D
                 </button>
               </div>
             </div>
             <div style={{ height: '240px' }}>
                {spsViewMode === '2D' ? (
                  <Line 
                    data={{
                      labels: data?.trends?.dates || [],
                      datasets: [{
                        data: data?.trends?.sps || [],
                        borderColor: '#10b981',
                        borderWidth: 3,
                        backgroundColor: (context) => {
                          const ctx = context.chart.ctx;
                          const gradient = ctx.createLinearGradient(0, 0, 0, 240);
                          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.3)');
                          gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                          return gradient;
                        },
                        fill: true,
                        tension: 0.4,
                        pointRadius: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false }, tooltip: { enabled: true } },
                      scales: {
                        x: { grid: { display: false }, ticks: { color: '#555', font: { size: 12 } } },
                        y: { 
                          min: 0, 
                          max: 5, 
                          grid: { color: 'rgba(255,255,255,0.03)', drawBorder: false }, 
                          ticks: { color: '#555', stepSize: 1 } 
                        }
                      }
                    }}
                  />
                ) : (
                  <Bar 
                    data={{
                      labels: data?.trends?.dates || [],
                      datasets: [{
                        data: data?.trends?.sps || [],
                        backgroundColor: (context) => {
                          const score = context.raw;
                          if (score > 4.1) return '#10b981';
                          if (score >= 3.6) return '#eab308';
                          return '#ef4444';
                        },
                        borderRadius: 6,
                        barThickness: 20,
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { 
                        legend: { display: false }, 
                        tooltip: { 
                          enabled: true,
                          backgroundColor: '#000',
                          titleColor: '#fff',
                          bodyColor: '#fff',
                          borderColor: '#1a1a1a',
                          borderWidth: 1,
                          padding: 12,
                          displayColors: false,
                          callbacks: {
                            label: (context) => `Score: ${context.parsed.y.toFixed(1)}`
                          }
                        } 
                      },
                      scales: {
                        x: { grid: { display: false }, ticks: { color: '#555', font: { size: 12 } } },
                        y: { 
                          min: 0, 
                          max: 5, 
                          grid: { color: 'rgba(255,255,255,0.03)', drawBorder: false }, 
                          ticks: { color: '#555', stepSize: 1 } 
                        }
                      }
                    }}
                  />
                )}
             </div>
          </div>

          <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
             <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '24px' }}>AI Insights</div>
             {(data?.insights || []).map((ins, i) => {
               const theme = ins.type === 'success' ? 'green' : ins.type === 'info' ? 'blue' : 'purple';
               const Icon = ins.type === 'success' ? TrendingUp : ins.type === 'info' ? Clock : Star;
               return (
                <div key={i} className={`insight-card ${theme}`}>
                  <div className="insight-icon-box">
                    <Icon size={22} />
                  </div>
                  <div className="insight-content">
                    <div className="insight-title">{ins.title}</div>
                    <div className="insight-desc">{ins.desc}</div>
                  </div>
                </div>
               );
             })}
          </div>
        </div>

        {/* Row 3: Fulfillment Performance & Health Alerts */}
        <div className="health-main-grid">
           {/* Fulfillment Performance */}
           <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px' }}>
                <span style={{ fontSize: '18px', fontWeight: '700' }}>Fulfillment Performance</span>
                <span style={{ fontSize: '12px', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>View Details <ChevronRight size={14} /></span>
              </div>
              
              <div className="fulfillment-sub-grid">
                 <div className="fulfillment-mini-card green">
                    <div className="mini-card-header">
                       <span className="mini-card-label">On-Time SLA</span>
                       <div className="mini-card-dot" style={{ backgroundColor: '#10b981' }} />
                    </div>
                    <div className="mini-card-value">{data?.shop_health?.on_time_delivery_rate || 'N/A'}</div>
                    <div className="mini-card-trend up">
                       <TrendingUp size={14} /> +2%
                    </div>
                 </div>

                 <div className="fulfillment-mini-card blue">
                    <div className="mini-card-header">
                       <span className="mini-card-label">Cancellation</span>
                       <div className="mini-card-dot" style={{ backgroundColor: '#3b82f6' }} />
                    </div>
                    <div className="mini-card-value">{data?.shop_health?.seller_fault_cancellation_rate || 'N/A'}</div>
                    <div className="mini-card-trend down">
                       <TrendingDown size={14} /> -0.3%
                    </div>
                 </div>

                 <div className="fulfillment-mini-card purple">
                    <div className="mini-card-header">
                       <span className="mini-card-label">Return Rate</span>
                       <div className="mini-card-dot" style={{ backgroundColor: '#a855f7' }} />
                    </div>
                    <div className="mini-card-value">{data?.shop_health?.non_buyer_fault_rr_rate || 'N/A'}</div>
                    <div className="mini-card-trend down">
                       <TrendingDown size={14} /> -0.1%
                    </div>
                 </div>
              </div>

              <div style={{ height: '240px', position: 'relative', marginBottom: '40px' }}>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#fff' }}>
                        <div style={{ width: '20px', height: '3px', backgroundColor: '#10b981', borderRadius: '2px' }} /> On-Time Rate
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#fff' }}>
                        <div style={{ width: '20px', height: '3px', backgroundColor: '#3b82f6', borderRadius: '2px' }} /> Cancellation Rate
                    </div>
                </div>
                <Line 
                    data={{
                        labels: data?.trends?.dates || [],
                        datasets: [
                            { 
                                label: 'On-Time Rate', 
                                data: data?.trends?.fulfillment_otd || [], 
                                borderColor: '#10b981', 
                                borderWidth: 3,
                                backgroundColor: (context) => {
                                    const ctx = context.chart.ctx;
                                    const gradient = ctx.createLinearGradient(0, 0, 0, 240);
                                    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)');
                                    gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                                    return gradient;
                                },
                                fill: true,
                                tension: 0.4, 
                                pointRadius: 0 
                            },
                            { 
                                label: 'Cancellation Rate', 
                                data: data?.trends?.fulfillment_cancellation || [], 
                                borderColor: '#3b82f6', 
                                borderWidth: 2,
                                tension: 0.4, 
                                pointRadius: 0 
                            }
                        ]
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                            x: { grid: { display: false }, ticks: { color: '#555' } },
                            y: { 
                                grid: { color: 'rgba(255,255,255,0.03)', drawBorder: false }, 
                                ticks: { color: '#555' } 
                            }
                        }
                    }}
                />
              </div>

              <div className="full-stats-grid">
                 <div className="full-stats-col">
                    <div className="full-stats-row">
                        <span className="full-stats-label">Return Rate</span>
                        <span className="full-stats-value">{data?.shop_health?.non_buyer_fault_rr_rate || '0%'}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Late Dispatch Rate</span>
                        <span className="full-stats-value">{data?.shop_health?.late_dispatch_rate || '0%'}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Seller-fault Cancellation Rate</span>
                        <span className="full-stats-value">{data?.shop_health?.seller_fault_cancellation_rate || '0%'}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Avg Response Time</span>
                        <span className="full-stats-value">{data?.shop_health?.average_response_time || '0h 0m'}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Negative review rate</span>
                        <span className="full-stats-value">{data?.shop_health?.negative_review || '0%'}</span>
                    </div>
                 </div>
                 <div className="full-stats-col">
                    <div className="full-stats-row">
                        <span className="full-stats-label">Shipped</span>
                        <span className="full-stats-value">{data?.fulfillment?.shipped || 0}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Completed</span>
                        <span className="full-stats-value">{data?.fulfillment?.completed || 0}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Pending</span>
                        <span className="full-stats-value">{data?.fulfillment?.pending || 0}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">To ship</span>
                        <span className="full-stats-value">{data?.fulfillment?.to_ship || 0}</span>
                    </div>
                    <div className="full-stats-row">
                        <span className="full-stats-label">Cancelled</span>
                        <span className="full-stats-value">{data?.fulfillment?.cancelled || 0}</span>
                    </div>
                 </div>
              </div>
           </div>

           {/* Health Alerts Sidebar */}
           <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
              <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '24px' }}>Health Alerts</div>
              <div className="insight-card green">
                 <div className="insight-icon-box" style={{ borderRadius: '50%' }}>
                    <CheckCircle2 size={18} />
                 </div>
                 <div className="insight-content">
                    <div className="insight-title" style={{ fontSize: '14px' }}>All Systems Healthy</div>
                    <div className="insight-desc" style={{ fontSize: '12px' }}>No critical alerts detected</div>
                 </div>
              </div>
              
              <div className="insight-card blue">
                 <div className="insight-icon-box" style={{ borderRadius: '50%' }}>
                    <Info size={18} />
                 </div>
                 <div className="insight-content">
                    <div className="insight-title" style={{ fontSize: '14px' }}>Response Time Optimal</div>
                    <div className="insight-desc" style={{ fontSize: '12px' }}>Maintaining 2h 15m average</div>
                 </div>
              </div>

              <div className="insight-card purple">
                 <div className="insight-icon-box" style={{ borderRadius: '50%' }}>
                    <TrendingUp size={18} />
                 </div>
                 <div className="insight-content">
                    <div className="insight-title" style={{ fontSize: '14px' }}>SPS Trending Up</div>
                    <div className="insight-desc" style={{ fontSize: '12px' }}>+0.3 improvement this month</div>
                 </div>
              </div>

              <div className="insight-card" style={{ borderColor: 'rgba(249, 115, 22, 0.2)', backgroundColor: '#1a0d08' }}>
                 <div className="insight-icon-box" style={{ backgroundColor: 'rgba(249, 115, 22, 0.15)', color: '#f97316', borderRadius: '50%' }}>
                    <Star size={18} />
                 </div>
                 <div className="insight-content">
                    <div className="insight-title" style={{ fontSize: '14px' }}>High Satisfaction</div>
                    <div className="insight-desc" style={{ fontSize: '12px' }}>94% customer satisfaction rate</div>
                 </div>
              </div>
           </div>
        </div>
        {/* Row 4: Support Funnel & Ticket Categories */}
        <div className="health-main-grid">
           {/* Support Ticket Funnel */}
           <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
              <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '32px' }}>Support Ticket Funnel</div>
              
              <div className="funnel-container">
                 <div className="funnel-step-group">
                    <div className="funnel-step-header">
                       <span>Tickets Opened</span>
                       <span className="funnel-step-value">342</span>
                    </div>
                    <div className="funnel-pill blue">100%</div>
                 </div>
                 
                 <div className="funnel-divider">
                    <ChevronDown size={24} />
                 </div>

                 <div className="funnel-step-group">
                    <div className="funnel-step-header">
                       <span>Responded</span>
                       <span className="funnel-step-value">335</span>
                    </div>
                    <div className="funnel-pill purple">98%</div>
                 </div>

                 <div className="funnel-divider">
                    <ChevronDown size={24} />
                 </div>

                 <div className="funnel-step-group">
                    <div className="funnel-step-header">
                       <span>Resolved</span>
                       <span className="funnel-step-value">318</span>
                    </div>
                    <div className="funnel-pill green">93%</div>
                 </div>

                 <div className="funnel-divider">
                    <ChevronDown size={24} />
                 </div>

                 <div className="funnel-step-group">
                    <div className="funnel-step-header">
                       <span>Converted Customers</span>
                       <span className="funnel-step-value">286</span>
                    </div>
                    <div className="funnel-pill orange">84%</div>
                 </div>
              </div>

              <div className="funnel-conversion">
                 <span style={{ fontSize: '14px', color: '#888' }}>Conversion Rate</span>
                 <span style={{ fontSize: '24px', fontWeight: '800', color: '#10b981' }}>84%</span>
              </div>
           </div>

           {/* Ticket Categories */}
           <div className={`health-card ${loading ? 'shimmer skeleton' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px' }}>
                <span style={{ fontSize: '18px', fontWeight: '700' }}>Ticket Categories</span>
                <span style={{ fontSize: '12px', color: '#3b82f6', cursor: 'pointer' }}>View All</span>
              </div>

              {[
                { label: 'Shipping & Delivery', value: 142, perc: 42, theme: 'blue', Icon: Package },
                { label: 'Product Questions', value: 98, perc: 29, theme: 'purple', Icon: Tag },
                { label: 'Returns & Refunds', value: 54, perc: 16, theme: 'orange', Icon: RotateCcw },
                { label: 'Payment Issues', value: 28, perc: 8, theme: 'green', Icon: CreditCard },
                { label: 'Other', value: 20, perc: 5, theme: 'pink', Icon: HelpCircle },
              ].map((cat, i) => (
                <div key={i} className="category-row">
                   <div className="category-header">
                      <div className="category-info">
                         <div className={`category-icon-bg ${cat.theme}`}>
                            <cat.Icon size={16} />
                         </div>
                         <span className="category-label">{cat.label}</span>
                      </div>
                      <div className="category-meta">
                         {cat.value} <span>{cat.perc}%</span>
                      </div>
                   </div>
                   <div className="category-bar-bg">
                      <div 
                         className="category-bar-fill" 
                         style={{ 
                            width: `${cat.perc}%`, 
                            backgroundColor: cat.theme === 'blue' ? '#3b82f6' : 
                                            cat.theme === 'purple' ? '#a855f7' : 
                                            cat.theme === 'orange' ? '#f59e0b' : 
                                            cat.theme === 'green' ? '#10b981' : '#ec4899'
                         }} 
                      />
                   </div>
                </div>
              ))}

              <div className="cat-footer-grid">
                 <div className="cat-stat-group">
                    <span className="cat-stat-label">Avg Resolution</span>
                    <span className="cat-stat-value">4.2h</span>
                 </div>
                 <div className="cat-stat-group">
                    <span className="cat-stat-label">First Response</span>
                    <span className="cat-stat-value">18m</span>
                 </div>
                 <div className="cat-stat-group" style={{ textAlign: 'right' }}>
                    <span className="cat-stat-label">CSAT Score</span>
                    <span className="cat-stat-value" style={{ color: '#10b981' }}>4.7/5</span>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  </UserLayout>
);
};

export default ShopHealthPage;
